import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { healthCyclesAPI } from '../../services/api'
import HealthReportSection from './HealthReportSection'
import CycleReportPdfButton from './CycleReportPdfButton'
import { errorCode, formatDate } from './cycleStatus'
import { fmtNum, humanizeCode, markerLabel, markerNames } from './healthReportFormat'

const tr = (t, key, code) => t(key, { defaultValue: humanizeCode(code) })

const decisionState = (rec) => (rec.status === 'done' ? 'done' : rec.is_accepted === true ? 'accepted' : rec.is_accepted === false ? 'rejected' : 'open')

function RecommendationRow({ rec, editable, onDecide, busy, names }) {
  const { t, i18n } = useTranslation()
  const state = decisionState(rec)
  const action = rec.measurable_action ?? {}
  const subject = action.subject
    ? (names[action.subject] ?? t(`cycleReports.health.metric.${action.subject}`, { defaultValue: markerLabel(names, action.subject) }))
    : null
  const n = (v) => (typeof v === 'number' ? fmtNum(v, i18n.language, 2) : v)
  const otherMarkers = (rec.target_markers ?? []).filter((m) => m.marker_code !== action.subject)
  const factors = [
    rec.evidence && t('cycleReports.health.rankEvidence', { value: tr(t, `cycleReports.health.evidenceLevel.${rec.evidence}`, rec.evidence) }),
    rec.cost && t('cycleReports.health.rankCost', { value: tr(t, `cycleReports.health.costLevel.${rec.cost}`, rec.cost) }),
    rec.domains?.length > 0 && t('cycleReports.health.rankDomains', { value: rec.domains.map((d) => tr(t, `cycleReports.health.domain.${d}`, d)).join(', ') }),
  ].filter(Boolean)
  const groupReason = rec.sequence_group ? t(`cycleReports.health.groupReason.${rec.sequence_group}`, { defaultValue: '' }) : ''
  return (
    <li className="border border-gray-200 rounded-md p-3 space-y-1" data-testid={`recommendation-${rec.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-gray-500">#{rec.position}</span>
        <span className="font-medium text-gray-900">{rec.title}</span>
        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs">{tr(t, `cycleReports.sequenceGroup.${rec.sequence_group}`, rec.sequence_group)}</span>
        <span className={`rounded px-2 py-0.5 text-xs ${state === 'rejected' ? 'bg-red-50 text-red-700' : state === 'open' ? 'bg-gray-100 text-gray-700' : 'bg-green-50 text-green-700'}`}>
          {t(`cycleReports.decision.${state}`)}
        </span>
      </div>
      {subject && action.to == null && action.from != null && (
        <p className="text-sm text-gray-700">
          {subject}: {n(action.from)}{action.unit ? ` ${action.unit}` : ''}
        </p>
      )}
      {subject && action.to != null && (
        <p className="text-sm text-gray-700">
          {subject}: {action.from != null ? `${n(action.from)} → ` : ''}{n(action.to) ?? '—'}{action.unit ? ` ${action.unit}` : ''}
        </p>
      )}
      {otherMarkers.length > 0 && (
        <p className="text-xs text-gray-600">
          {t('cycleReports.health.targetMarkers')}: {otherMarkers.map((m) => `${markerLabel(names, m.marker_code, m.marker_name)} (${tr(t, `cycleReports.direction.${m.direction}`, m.direction)})`).join(', ')}
        </p>
      )}
      {(factors.length > 0 || groupReason) && (
        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer">{t('cycleReports.health.whyRank')}</summary>
          {groupReason && <p className="mt-1">{groupReason}</p>}
          {factors.length > 0 && <p>{factors.join(' · ')}</p>}
        </details>
      )}
      {editable && state !== 'done' && rec.recommendation_id && (
        <div className="flex gap-2 pt-1">
          <button type="button" className="btn-outline text-xs" disabled={busy} aria-pressed={rec.is_accepted === true}
            onClick={() => onDecide(rec, rec.is_accepted === true ? null : true)}>
            {t('cycleReports.decision.accept')}
          </button>
          <button type="button" className="btn-outline text-xs" disabled={busy} aria-pressed={rec.is_accepted === false}
            onClick={() => onDecide(rec, rec.is_accepted === false ? null : false)}>
            {t('cycleReports.decision.reject')}
          </button>
        </div>
      )}
    </li>
  )
}

function Contents({ sections }) {
  const { t } = useTranslation()
  if (sections.length < 4) return null
  const jump = (id) => document.getElementById(`health-section-${id}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  return (
    <nav aria-label={t('cycleReports.health.contents')} className="flex flex-wrap gap-2" data-testid="health-report-contents">
      {sections.map((s) => (
        <button key={s.id} type="button" onClick={() => jump(s.id)}
          className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700 hover:bg-gray-100">
          {tr(t, `cycleReports.health.sections.${s.id}`, s.id)}
        </button>
      ))}
    </nav>
  )
}

// VH-228: the cycle health report of one version — sections in order, ranked
// recommendations (accept / reject on drafts), PDF export of that version.
export function HealthReportTab({ cycleId, versionNumber, onOpenNutrition = null }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const printRef = useRef(null)
  const [error, setError] = useState(null)
  const queryKey = ['healthCycleHealthReport', cycleId, versionNumber]

  const reportQuery = useQuery({
    queryKey,
    queryFn: () => healthCyclesAPI.getHealthReport(cycleId, versionNumber),
    select: (r) => r.data,
    enabled: !!cycleId && !!versionNumber,
    retry: (count, err) => err?.response?.status !== 404 && count < 2,
  })

  const decide = useMutation({
    mutationFn: ({ rec, value }) => healthCyclesAPI.decideRecommendation(cycleId, rec.recommendation_id, value),
    onSuccess: () => {
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['healthCycleHealthReport', cycleId] })
      queryClient.invalidateQueries({ queryKey: ['healthCycleVersion', cycleId] })
    },
    onError: (err) => setError(t(`cycleReports.errors.${errorCode(err)}`, t('healthCycle.errors.generic'))),
  })

  if (reportQuery.isLoading) return <div className="h-24 rounded-lg border border-gray-200 animate-pulse" />
  if (reportQuery.isError) {
    const code = errorCode(reportQuery.error)
    return (
      <p className="text-sm text-gray-600 py-6 text-center" data-testid="health-report-unavailable">
        {t(`cycleReports.errors.${code}`, t('healthCycle.errors.generic'))}
      </p>
    )
  }
  const data = reportQuery.data
  const finalized = !!data.finalized_at
  const recommendations = [...(data.recommendations ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
  const sections = data.report?.sections ?? []
  const names = markerNames(sections, recommendations.flatMap((r) => r.target_markers ?? []))
  const recommendationTitles = Object.fromEntries(recommendations.map((r) => [r.key, r.title]))
  const stamp = t('cycleReports.pdf.stamp', {
    number: data.number,
    date: finalized ? formatDate(data.finalized_at, i18n.language) : t('healthCycle.status.draft'),
  })

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">{stamp}</p>
        {finalized && (
          <CycleReportPdfButton targetRef={printRef} stamp={stamp} fileName={`health-cycle-${cycleId}-v${data.number}.pdf`} />
        )}
      </div>
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <Contents sections={sections} />
      <div ref={printRef} className="space-y-3 bg-surface">
        <h3 className="text-lg font-semibold text-gray-900">{t('cycleReports.health.title')}</h3>
        {sections.map((section) => (
          <HealthReportSection key={section.id} section={section} onOpenNutrition={onOpenNutrition}
            recommendationCount={recommendations.length} names={names} recommendationTitles={recommendationTitles}>
            {section.id === 'levers' && recommendations.length > 0 && (
              <ol className="space-y-2">
                {recommendations.map((rec) => (
                  <RecommendationRow key={rec.key} rec={rec} editable={!finalized} busy={decide.isPending} names={names}
                    onDecide={(r, value) => decide.mutate({ rec: r, value })} />
                ))}
              </ol>
            )}
          </HealthReportSection>
        ))}
      </div>
    </div>
  )
}

export default HealthReportTab
